import * as Sentry from '@sentry/react';

/**
 * Loads the mapbox-gl chunk with retries.
 *
 * The screens turn their display on at 05:00 while the network is still down,
 * and a chunk fetch that fails then stays failed for the whole page lifetime
 * unless something retries (VIU-703). One load is shared by every caller; after
 * the last failed attempt the loader forgets it, so the next map mount starts
 * a new round of attempts.
 *
 * Browsers keep a failed module fetch in the page's module map, so importing
 * the same URL again fails without a request. Every attempt after the first
 * therefore imports the chunk URL from the error message with a `retry` query
 * that has not been used before on this page, also across rounds. When no
 * message has carried a URL (Safari) the plain import is all there is.
 */

export type MapboxModule = typeof import('mapbox-gl');

/** Waits before the second, third and fourth attempt. */
export const RETRY_DELAYS_MS = [2000, 4000, 8000];

export type ImportMapbox = () => Promise<MapboxModule>;
export type ImportUrl = (url: string) => Promise<MapboxModule>;
type Wait = (ms: number) => Promise<void>;

const sleep: Wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const importUrlDefault: ImportUrl = (url) => import(/* @vite-ignore */ url);

/** The failing chunk URL, as Chrome and Firefox put it in the message. */
export const chunkUrlFromError = (error: unknown): string | undefined =>
  error instanceof Error
    ? /https?:\/\/[^\s'"]+/.exec(error.message)?.[0]
    : undefined;

export const retryUrl = (url: string, retry: number): string => {
  const next = new URL(url);
  next.searchParams.set('retry', String(retry));
  return next.href;
};

export const createMapboxLoader = (
  importMapbox: ImportMapbox,
  importUrl: ImportUrl = importUrlDefault,
  wait: Wait = sleep,
): (() => Promise<MapboxModule>) => {
  let pending: Promise<MapboxModule> | undefined;
  /** The chunk URL from the last failure; undefined until a message had one. */
  let chunkUrl: string | undefined;
  /** Retry URLs imported so far on this page, so no URL is imported twice. */
  let retries = 0;

  const importNext = (): Promise<MapboxModule> => {
    if (chunkUrl === undefined) return importMapbox();
    retries += 1;
    return importUrl(retryUrl(chunkUrl, retries));
  };

  const load = async (): Promise<MapboxModule> => {
    for (let attempt = 1; ; attempt += 1) {
      try {
        return await importNext();
      } catch (error) {
        chunkUrl = chunkUrlFromError(error) ?? chunkUrl;
        const delay = RETRY_DELAYS_MS[attempt - 1];
        if (delay === undefined) {
          Sentry.captureException(error, { extra: { attempts: attempt } });
          throw error;
        }
        const line = `mapbox-gl load failed (attempt ${attempt}), retrying in ${delay / 1000} s`;
        console.warn(`[viu] ${line}`);
        Sentry.addBreadcrumb({
          category: 'mapbox',
          message: line,
          level: 'warning',
          data: { error: String(error) },
        });
        await wait(delay);
      }
    }
  };

  return () => {
    if (!pending) {
      pending = load().catch((error: unknown) => {
        pending = undefined;
        throw error;
      });
    }
    return pending;
  };
};

export const loadMapbox = createMapboxLoader(() => import('mapbox-gl'));
